import type { FastifyReply } from 'fastify'
import { tryGetBillingService } from '../modules/billing/billing.container.js'
import { ApiResponse } from './response'
import { createLogger } from '../common/utils/logger.js'

const logger = createLogger('PremiumUtils')

/**
 * Check if user has an active premium subscription
 * @returns { isPremium: boolean, tier: string }
 */
export async function checkPremiumStatus(userId: string): Promise<{ isPremium: boolean; tier: string }> {
  const billingService = tryGetBillingService()
  if (!billingService) {
    // No billing service - all users are free tier
    return { isPremium: false, tier: 'free' }
  }

  try {
    const summary = await billingService.getSubscriptionSummary(userId)
    if (!summary) {
      return { isPremium: false, tier: 'free' }
    }

    // Check if subscription is active/trialing AND tier is premium
    const isActive = summary.subscription.status === 'active' || summary.subscription.status === 'trialing'
    const isPremium = isActive && summary.subscription.tier === 'premium'

    return {
      isPremium,
      tier: summary.subscription.tier
    }
  } catch (error) {
    logger.error('Error checking premium status', { userId, error: (error as Error).message })
    // Fail closed - treat as free user on error
    return { isPremium: false, tier: 'free' }
  }
}

/**
 * Middleware to require premium subscription
 * Returns 403 Forbidden if user is not premium
 */
export async function requirePremium(userId: string, reply: FastifyReply, feature: string): Promise<boolean> {
  const { isPremium } = await checkPremiumStatus(userId)
  
  if (!isPremium) {
    ApiResponse.forbidden(reply, `${feature} requires a premium subscription`)
    return false
  }

  return true
}
